import { useEffect, useRef } from "react";
import "./Cursor.css";

/**
 * A two-part pointer: a hard dot that tracks exactly, and a ring that lags.
 *
 * The dot is where the cursor *is* — it has to be frame-accurate or clicking
 * feels broken. The ring is where the cursor has *been*, and the distance
 * between the two is the only thing here doing any work: it stretches when you
 * move fast and collapses when you stop, which is what gives the pointer a
 * sense of mass without slowing anything down.
 *
 * Over anything interactive the ring swells and inverts, so a link announces
 * itself before you arrive rather than after you hover it.
 *
 * Everything is written straight to `style.transform` from one rAF loop rather
 * than going through React state. A cursor that re-renders a component tree 60
 * times a second is a cursor that stutters the moment the page is busy — which
 * is exactly when a reader is most likely to be moving it.
 */

/** How much of the remaining gap the ring closes per frame. Lower = heavier. */
const RING_LERP = 0.18;
/** Selector for everything that should make the ring react. */
const INTERACTIVE =
    'a, button, input, textarea, select, summary, [role="button"], [data-cursor="focus"]';

export default function Cursor() {
    const dotRef = useRef<HTMLDivElement>(null);
    const ringRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const dot = dotRef.current;
        const ring = ringRef.current;
        if (!dot || !ring) return;

        // A custom cursor is meaningless without a cursor to customise, and
        // actively hostile under reduced-motion: the whole point of it is the
        // lag, and a lag-free version is just a worse arrow.
        if (
            window.matchMedia("(hover: none)").matches ||
            window.matchMedia("(pointer: coarse)").matches ||
            window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ) {
            return;
        }

        const root = document.documentElement;
        // Only now — with a replacement definitely on screen — is it safe to
        // take the native arrow away. See index.css.
        root.classList.add("has-custom-cursor");

        const target = { x: -100, y: -100 };
        const pos = { x: -100, y: -100 };
        let scale = 1;
        let scaleTarget = 1;
        let first = true;

        let raf = 0;
        const frame = () => {
            pos.x += (target.x - pos.x) * RING_LERP;
            pos.y += (target.y - pos.y) * RING_LERP;
            scale += (scaleTarget - scale) * 0.16;

            // Distance the ring is still behind the dot, mapped to a squash
            // along the axis of travel. `atan2` gives the direction to lean
            // into, so the ring elongates the way it is actually moving.
            const dx = target.x - pos.x;
            const dy = target.y - pos.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            const stretch = Math.min(dist / 130, 0.42);
            const angle = (Math.atan2(dy, dx) * 180) / Math.PI;

            ring.style.transform =
                `translate3d(${pos.x}px, ${pos.y}px, 0) ` +
                `translate(-50%, -50%) ` +
                `rotate(${angle}deg) ` +
                `scale(${scale * (1 + stretch)}, ${scale * (1 - stretch)})`;

            raf = requestAnimationFrame(frame);
        };

        const onMove = (e: PointerEvent) => {
            target.x = e.clientX;
            target.y = e.clientY;

            // The dot never lerps — it is the pointer, not a comment on it.
            dot.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0) translate(-50%, -50%)`;

            if (first) {
                // Land both parts where the pointer already is instead of
                // letting the ring fly in from the corner on the first move.
                first = false;
                pos.x = e.clientX;
                pos.y = e.clientY;
                root.classList.add("cursor-ready");
            }

            // `closest` rather than a match on the target itself: the thing
            // under the pointer inside a button is usually a span or an svg,
            // and testing only that would leave most of the site's controls
            // silently non-reactive.
            const el = e.target as Element | null;
            scaleTarget = el?.closest?.(INTERACTIVE) ? 2.6 : 1;
            ring.classList.toggle("is-focus", scaleTarget !== 1);
        };

        const onDown = () => {
            ring.classList.add("is-down");
        };
        const onUp = () => {
            ring.classList.remove("is-down");
        };

        // Leaving the document entirely should hide both parts, or they park
        // at the last known edge position and sit there like a dead pixel.
        const onLeave = () => root.classList.remove("cursor-ready");
        const onEnter = () => root.classList.add("cursor-ready");

        window.addEventListener("pointermove", onMove, { passive: true });
        window.addEventListener("pointerdown", onDown, { passive: true });
        window.addEventListener("pointerup", onUp, { passive: true });
        document.addEventListener("pointerleave", onLeave);
        document.addEventListener("pointerenter", onEnter);
        raf = requestAnimationFrame(frame);

        return () => {
            cancelAnimationFrame(raf);
            root.classList.remove("has-custom-cursor", "cursor-ready");
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerdown", onDown);
            window.removeEventListener("pointerup", onUp);
            document.removeEventListener("pointerleave", onLeave);
            document.removeEventListener("pointerenter", onEnter);
        };
    }, []);

    return (
        <>
            <div ref={ringRef} className="cursor-ring" aria-hidden="true" />
            <div ref={dotRef} className="cursor-dot" aria-hidden="true" />
        </>
    );
}
